import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Fingerprint, Sparkles, CalendarClock, Send, Settings2, LogOut, ChevronsUpDown, Plus, Check, Layers, Building2 } from "lucide-react";

const NAV = [
  { id: "brand", label: "Brand DNA", icon: Fingerprint },
  { id: "create", label: "Create", icon: Sparkles },
  { id: "review", label: "Review", icon: Layers },
  { id: "calendar", label: "Calendar", icon: CalendarClock },
  { id: "publish", label: "Publish", icon: Send },
  { id: "settings", label: "Settings", icon: Settings2 },
];

function useOutsideClose(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, setOpen]);
  return ref;
}

export function WorkspaceSidebar({
  section, setSection, appName = "HDG Creative Studio", usage,
  workspaces = [], activeWsId, onSwitchWorkspace, onCreateWorkspace,
  poweredBy, poweredByUrl, onLogout, brands = [], currentBrandId, currentName, onLoadBrand, onNewBrand,
}) {
  const [wsOpen, setWsOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const wsRef = useOutsideClose(wsOpen, setWsOpen);
  const switcherRef = useOutsideClose(switcherOpen, setSwitcherOpen);

  const activeWs = workspaces.find((w) => w.id === activeWsId);
  const tier = usage?.tier || activeWs?.tier || "trial";
  const allowance = usage?.allowance ?? activeWs?.allowance ?? 0;
  const used = usage?.postsUsed ?? 0;
  const pct = allowance ? Math.min(100, Math.round((used / allowance) * 100)) : 0;

  const submitNew = () => {
    const n = newName.trim();
    if (!n) return;
    onCreateWorkspace?.(n);
    setNewName("");
    setWsOpen(false);
  };

  return (
    <aside
      data-testid="workspace-sidebar"
      className="w-[68px] lg:w-64 flex-shrink-0 flex flex-col"
      style={{ background: "var(--panel)", borderRight: "1px solid var(--cardborder)", height: "100vh" }}
    >
      <div className="flex items-center gap-3 px-3 lg:px-5" style={{ height: 64, borderBottom: "1px solid var(--cardborder)" }}>
        <Link to="/app" data-testid="sidebar-home-link" aria-label="HDG Creative Studio — home" className="flex items-center gap-3" style={{ textDecoration: "none", cursor: "pointer" }}>
          <img src="/hdg-logo.jpg" alt="HeyDesignGuy" data-testid="sidebar-logo" style={{ height: 34, width: 34, objectFit: "contain", borderRadius: 9, background: "#fff", border: "1px solid var(--cardborder)", flexShrink: 0 }} />
          <span className="hidden lg:block font-display" style={{ fontWeight: 700, fontSize: 15, color: "var(--tprimary)", letterSpacing: "-0.01em", lineHeight: 1.15 }}>{appName}</span>
        </Link>
      </div>

      {/* workspace (client) switcher — agency master view */}
      <div className="hidden lg:block px-3 pt-3" style={{ position: "relative" }} ref={wsRef}>
        <div className="font-mono" style={{ fontSize: 9.5, letterSpacing: "0.1em", color: "var(--tmuted)", textTransform: "uppercase", marginBottom: 6, paddingLeft: 2 }}>Workspace</div>
        <button
          data-testid="workspace-switcher"
          onClick={(e) => { e.stopPropagation(); setSwitcherOpen(false); setWsOpen((v) => !v); }}
          className="w-full flex items-center justify-between gap-2"
          style={{ padding: "9px 11px", borderRadius: 9, border: "1px solid var(--cardborder)", background: "var(--surface)", cursor: "pointer", color: "var(--tprimary)" }}
        >
          <span className="flex items-center gap-2" style={{ minWidth: 0 }}>
            <Building2 size={14} style={{ color: "var(--orange)", flexShrink: 0 }} />
            <span className="font-display" style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130, textAlign: "left" }}>
              {activeWs?.name || "Select workspace"}
            </span>
          </span>
          <ChevronsUpDown size={15} style={{ color: "var(--tmuted)", flexShrink: 0 }} />
        </button>
        {wsOpen && (
          <div data-testid="workspace-menu" className="thin-scroll" style={{ position: "absolute", left: 12, right: 12, top: 72, zIndex: 60, background: "var(--panel)", border: "1px solid var(--cardborder)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: 6, maxHeight: 340, overflowY: "auto" }}>
            {workspaces.map((w) => (
              <button
                key={w.id}
                data-testid={`workspace-option-${w.id}`}
                onClick={() => { onSwitchWorkspace?.(w.id); setWsOpen(false); }}
                className="ws-nav-item"
                style={{ fontSize: 13 }}
              >
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>{w.name}</span>
                <span className="font-mono" style={{ fontSize: 9.5, color: "var(--tmuted)", textTransform: "capitalize", marginRight: 6 }}>{w.tier}</span>
                {w.id === activeWsId && <Check size={14} style={{ color: "var(--orange)", flexShrink: 0 }} />}
              </button>
            ))}
            <div style={{ height: 1, background: "var(--cardborder)", margin: "6px 4px" }} />
            <div style={{ display: "flex", gap: 6, padding: "4px" }}>
              <input
                data-testid="workspace-name-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitNew(); }}
                placeholder="New client name"
                className="dim-input font-body"
                style={{ flex: 1, fontSize: 12, padding: "7px 9px", minHeight: 0 }}
              />
              <button data-testid="workspace-create-button" onClick={submitNew} className="btn-primary font-mono" style={{ fontSize: 11, padding: "0 10px" }}>
                <Plus size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* brand switcher (within workspace) */}
      <div className="hidden lg:block px-3 pt-3" style={{ position: "relative" }} ref={switcherRef}>
        <button
          data-testid="sidebar-brand-switcher"
          onClick={(e) => { e.stopPropagation(); setWsOpen(false); setSwitcherOpen((v) => !v); }}
          className="w-full flex items-center justify-between gap-2"
          style={{ padding: "9px 11px", borderRadius: 9, border: "1px solid var(--cardborder)", background: "var(--surface)", cursor: "pointer", color: "var(--tprimary)" }}
        >
          <span className="font-display" style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150, textAlign: "left" }}>
            {currentName || "Select a brand"}
          </span>
          <ChevronsUpDown size={15} style={{ color: "var(--tmuted)", flexShrink: 0 }} />
        </button>
        {switcherOpen && (
          <div data-testid="sidebar-brand-menu" className="thin-scroll" style={{ position: "absolute", left: 12, right: 12, top: 52, zIndex: 50, background: "var(--panel)", border: "1px solid var(--cardborder)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: 6, maxHeight: 300, overflowY: "auto" }}>
            {brands.length === 0 && <div className="font-body" style={{ fontSize: 12, color: "var(--tmuted)", padding: "8px 10px" }}>No saved brands yet</div>}
            {brands.map((b) => (
              <button
                key={b.id}
                data-testid={`switcher-brand-${b.id}`}
                onClick={() => { onLoadBrand?.(b.id); setSwitcherOpen(false); }}
                className="ws-nav-item"
                style={{ fontSize: 13 }}
                title={b.siteName || b.brandName || b.host}
              >
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.siteName || b.brandName || b.host}</span>
                {b.id === currentBrandId && <Check size={14} style={{ color: "var(--orange)", flexShrink: 0 }} />}
              </button>
            ))}
            <div style={{ height: 1, background: "var(--cardborder)", margin: "6px 4px" }} />
            <button data-testid="switcher-new-brand" onClick={() => { onNewBrand?.(); setSwitcherOpen(false); }} className="ws-nav-item" style={{ fontSize: 13, fontWeight: 600 }}>
              <Plus size={15} /> New Brand
            </button>
          </div>
        )}
      </div>

      <nav className="flex-1 flex flex-col gap-1 px-2 lg:px-3 py-4">
        {NAV.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`sidebar-${id}-link`}
            onClick={() => setSection(id)}
            className={`ws-nav-item justify-center lg:justify-start ${section === id ? "active" : ""}`}
            title={label}
          >
            <Icon size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span className="hidden lg:inline">{label}</span>
          </button>
        ))}
      </nav>

      <div className="px-2 lg:px-3 py-4" style={{ borderTop: "1px solid var(--cardborder)" }}>
        {/* allowance meter */}
        <div className="hidden lg:block px-3 mb-3" data-testid="sidebar-allowance">
          <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
            <span className="font-mono" style={{ fontSize: 10, letterSpacing: "0.06em", color: "var(--tmuted)", textTransform: "uppercase" }}>{tier} · posts</span>
            <span className="font-mono" style={{ fontSize: 10, color: "var(--tsecondary)" }} data-testid="sidebar-allowance-readout">{used}/{allowance}</span>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: "var(--surface)", overflow: "hidden", border: "1px solid var(--cardborder)" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "var(--err)" : "var(--orange)", transition: "width 0.3s ease" }} />
          </div>
        </div>
        <button data-testid="sidebar-logout" onClick={onLogout} className="ws-nav-item justify-center lg:justify-start" title="Log out">
          <LogOut size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
          <span className="hidden lg:inline">Log out</span>
        </button>
        {poweredBy && (
          <a href={poweredByUrl} target="_blank" rel="noreferrer" className="hidden lg:block app-link font-body" style={{ fontSize: 11, padding: "8px 12px 0" }}>
            {poweredBy}
          </a>
        )}
      </div>
    </aside>
  );
}
